import React, { useContext, useRef, useState } from "react";
import {
  Icon,
  IconButton,
  ImageList,
  ImageListItem,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { isNil, isEmpty, isArray, split } from "lodash";
import { useTranslation } from "react-i18next";

import { commonIcon, useStyles } from "../../pages/HomeStyles";
import { useAccessesStore } from "../../../../core/stores/AccessesStore";
import AddNewBandDialog from "./AddNewBandDialog";
import ReturnToDefaultDialog from "../returnToDefault/ReturnToDefaultDialog";
import { useRequestHeaders } from "../../../../core/hooks/useRequestHeaders";
import { AuthContext } from "../../../../core/providers/AuthContext";
import { ArrowBack, ArrowForward } from "@material-ui/icons";

const FUNCTIONALITY_ENABLED = process.env.REACT_APP_FAB_ACTIONS === "y";

const BG_IMAGES_LIST = [
  "default-bg.png",
  "1-bg.png",
  "2-bg.png",
  "3-bg.png",
  "4-bg.png",
  "5-bg.webp",
  "6-bg.jpg",
  "7-bg.jpg",
  "8-bg.jpg",
  "9-bg.jpg",
  "10-bg.jpg",
  "11-bg.jpg",
  "12-bg.png",
  "13-bg.jpg",
  "14-bg.jpg",
  "15-bg.jpg",
  "16-bg.jpg",
  "17-bg.jpg",
  "18-bg.jpg",
];

export default function FloatingActions() {
  const { t } = useTranslation();
  const classes = useStyles();
  const { logout } = useContext(AuthContext);
  const REQUEST_HEADERS = useRequestHeaders();

  const imageListContainerRef = useRef(null);

  const [openNewBandDialog, setOpenNewBandDialog] = useState(false);
  const [openReturnToDefaultDialog, setOpenReturnToDefaultDialog] =
    useState(false);
  const [showImageList, setShowImageList] = useState(false);

  const {
    dashboardSections,
    toggleEditing,
    isEditing,
    menuItems,
    cancelEditing,
    saveActualDashboard,
    isCustomHome,
    toggleAllBands,
    allHidden,
    setBackgroundImg,
    backgroundImg,
  } = useAccessesStore((state) => ({
    dashboardSections: state.dashboardSections,
    toggleEditing: state.toggleEditing,
    isEditing: state.isEditing,
    menuItems: state.menuItems,
    cancelEditing: state.cancelEditing,
    saveActualDashboard: state.saveActualDashboard,
    isCustomHome: state.isCustomHome,
    toggleAllBands: state.toggleAllBands,
    allHidden: state.allHidden,
    setBackgroundImg: state.setBackgroundImg,
    backgroundImg: state.backgroundImg,
  }));

  const handleOpenNewBandDialog = (to) => {
    setOpenNewBandDialog(to);
  };

  const handleReturnToDefaultDialog = (to) => {
    setOpenReturnToDefaultDialog(to);
  };

  const handleMainButton = () => {
    if (isEditing) {
      saveActualDashboard({
        t,
        REQUEST_HEADERS,
        logout,
      });
    } else {
      toggleEditing();
    }
  };

  const handleNextImage = () => {
    if (imageListContainerRef.current) {
      imageListContainerRef.current.scrollBy({
        left: 240,
        behavior: "smooth",
      });
    }
  };

  const handlePrevImage = () => {
    if (imageListContainerRef.current) {
      imageListContainerRef.current.scrollBy({
        left: -240,
        behavior: "smooth",
      });
    }
  };

  if (!FUNCTIONALITY_ENABLED) {
    return null;
  }

  return (
    <>
      {showImageList && (
        <div className={classes.imageListContainer}>
          <IconButton
            className={classes.navigationButtonLeft}
            onClick={handlePrevImage}
          >
            <ArrowBack />
          </IconButton>
          <ImageList
            className={classes.imageList}
            cols={3.5}
            gap={8}
            ref={imageListContainerRef}
          >
            {BG_IMAGES_LIST.map((bgImgID) => {
              const nameBgImg = split(bgImgID, "-bg")[0];
              return (
                <ImageListItem
                  key={bgImgID}
                  className={
                    bgImgID === backgroundImg
                      ? classes.selectedBgImg
                      : classes.bgImg
                  }
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/assets/img/home-bg/${bgImgID}`}
                    alt={bgImgID}
                    className={classes.imageListItem}
                    onClick={() => setBackgroundImg(bgImgID)}
                  />
                  <Typography className={classes.typoBgImgTagName}>
                    {nameBgImg}
                  </Typography>
                </ImageListItem>
              );
            })}
          </ImageList>
          <IconButton
            className={classes.navigationButtonRight}
            onClick={handleNextImage}
          >
            <ArrowForward />
          </IconButton>
        </div>
      )}

      {isEditing && (
        <Tooltip title={t("CUSTOM_HOME_CHANGE_BG")}>
          <IconButton
            className={classes.fabChangeBgImg}
            onClick={() => setShowImageList((prev) => !prev)}
          >
            <Icon
              className={"fas fa-images"}
              style={{ ...commonIcon, color: "#FFF" }}
            />
          </IconButton>
        </Tooltip>
      )}
      {isEditing && (
        <Tooltip
          title={
            allHidden
              ? t("CUSTOM_HOME_FAB_SHOW_ALL")
              : t("CUSTOM_HOME_FAB_HIDE_ALL")
          }
        >
          <IconButton
            className={classes.fabToggleAll}
            onClick={() => toggleAllBands()}
          >
            <Icon
              className={allHidden ? "fas fa-eye" : "fas fa-eye-slash"}
              style={{ ...commonIcon, color: "#FFF" }}
            />
          </IconButton>
        </Tooltip>
      )}

      {isEditing && (
        <Tooltip title={t("CUSTOM_HOME_ADD_BAND")}>
          <IconButton
            className={classes.fabAddBand}
            onClick={() => handleOpenNewBandDialog(true)}
          >
            <Icon
              className="far fa-plus-square"
              style={{ ...commonIcon, color: "#FFF" }}
            />
          </IconButton>
        </Tooltip>
      )}

      {isEditing && (
        <Tooltip title={t("CUSTOM_HOME_FAB_RETURN_DEF")}>
          <IconButton
            className={classes.fabRestore}
            onClick={() => handleReturnToDefaultDialog(true)}
          >
            <Icon
              className="fas fa-trash"
              style={{ ...commonIcon, color: "#FFF" }}
            />
          </IconButton>
        </Tooltip>
      )}

      {isEditing && (
        <Tooltip title={t("FORM_CANCEL")}>
          <IconButton
            className={classes.fabCancel}
            onClick={() => {
              setShowImageList(false);
              cancelEditing();
            }}
          >
            <Icon
              className="fas fa-times"
              style={{ ...commonIcon, color: "#FFF" }}
            />
          </IconButton>
        </Tooltip>
      )}

      {!isNil(dashboardSections) && !isEmpty(dashboardSections) && (
        <Tooltip
          title={
            isEditing ? t("CUSTOM_HOME_FAB_SAVE") : t("CUSTOM_HOME_FAB_EDIT")
          }
        >
          <IconButton className={classes.fab} onClick={handleMainButton}>
            <Icon
              className={isEditing ? "fas fa-save" : "fas fa-edit"}
              style={{ ...commonIcon, color: "#FFF" }}
            >
              {isCustomHome === true && (
                <Typography className={classes.customTypoLabel}>
                  CUSTOM
                </Typography>
              )}
            </Icon>
          </IconButton>
        </Tooltip>
      )}

      {isEditing &&
        openNewBandDialog &&
        !isNil(menuItems) &&
        !isEmpty(menuItems) &&
        isArray(menuItems) && (
          <AddNewBandDialog
            open={openNewBandDialog}
            handleOpenNewBandDialog={handleOpenNewBandDialog}
            menuItems={menuItems}
          />
        )}

      {openReturnToDefaultDialog && (
        <ReturnToDefaultDialog
          open={openReturnToDefaultDialog}
          handleReturnToDefaultDialog={handleReturnToDefaultDialog}
        />
      )}
    </>
  );
}
